import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Logo from '../../../src/assets/images/dwlogo_wide.png';
import LogoHeader from '../../shared/layout/components/misc/LogoHeader';
import LanguageSelector from '../../shared/layout/components/misc/LanguageSelector';
import { AuthContainer } from "../../shared/layout/components/containers/AuthContainer";
import GlassCard from "../../shared/layout/components/cards/GlassCard";
import GradientBackground from "../../shared/layout/components/containers/GradientBackground";
import MainButton from '../../shared/layout/buttons/MainButton';
import { axiosInstance } from '../../services/Http/Api';

const ForgotPasswordPage = () => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const initialCode = searchParams.get('code') || '';
    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [code, setCode] = useState(initialCode);
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [step, setStep] = useState(initialCode ? 'reset' : 'request');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('selectedLanguage') || 'en');
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            if (step === 'request') {
                await axiosInstance.post('/password/forgot', { email });
                setStep('reset');
                setMessage(t('forgotPasswordPage.code_sent'));
            } else {
                if (password !== passwordConfirmation) {
                    setError(t('forgotPasswordPage.password_mismatch'));
                    return;
                }
                await axiosInstance.post('/password/reset', {
                    email,
                    code,
                    password,
                    password_confirmation: passwordConfirmation,
                });
                setMessage(t('forgotPasswordPage.password_changed'));
                setTimeout(() => navigate('/login'), 1800);
            }
        } catch (err) {
            setError(t('forgotPasswordPage.reset_link_error'));
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = (language) => {
        setSelectedLanguage(language);
        localStorage.setItem('selectedLanguage', language);
        i18n.changeLanguage(language);
    };

    return (
        <GradientBackground>
            <GlassCard>
                <HeaderTop>
                    <div />
                    <LanguageSelector
                        selectedLanguage={selectedLanguage}
                        onLanguageChange={handleLanguageChange}
                        align="flex-end"
                    />
                </HeaderTop>
                <LogoHeader logoSrc={Logo} title="DigiWarranty" />

                <HeaderSection>
                    <h2>{t('forgotPasswordPage.title')}</h2>
                    <p>{t(step === 'request' ? 'forgotPasswordPage.instructions' : 'forgotPasswordPage.reset_instructions')}</p>
                </HeaderSection>

                {message && <SuccessMessage>{message}</SuccessMessage>}
                {error && <ErrorMessage>{error}</ErrorMessage>}

                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <label>{t('forgotPasswordPage.email_label')}</label>
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </FormGroup>
                    {step === 'reset' && <>
                        <FormGroup>
                            <label>{t('forgotPasswordPage.code_label')}</label>
                            <input inputMode="numeric" pattern="[0-9]{6}" maxLength="6" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
                        </FormGroup>
                        <FormGroup>
                            <label>{t('forgotPasswordPage.password_label')}</label>
                            <input type="password" minLength="8" value={password} onChange={(e) => setPassword(e.target.value)} required />
                        </FormGroup>
                        <FormGroup>
                            <label>{t('forgotPasswordPage.password_confirmation_label')}</label>
                            <input type="password" minLength="8" value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} required />
                        </FormGroup>
                    </>}
                    <MainButton type="submit" disabled={loading} style={{ width: '100%' }}>
                        {loading ? t('forgotPasswordPage.loading') : t(step === 'request' ? 'forgotPasswordPage.submit_button' : 'forgotPasswordPage.reset_button')}
                    </MainButton>
                </Form>

                <FooterLink onClick={() => navigate('/login')}>
                    {t('forgotPasswordPage.back_to_login')}
                </FooterLink>
            </GlassCard>
        </GradientBackground>
    );
};

// Styled Components
const HeaderSection = styled.div`
    text-align: center;
    margin-bottom: 2rem;

    h2 {
        font-size: 1.5rem;
        font-weight: 700;
        color: #1a0621;
        margin-bottom: 0.5rem;
    }

    p {
        color: #6b7280;
        font-size: 0.95rem;
        line-height: 1.5;
    }
`;

const HeaderTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.75rem;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
    }

    input {
        padding: 0.75rem 1rem;
        border: 1.5px solid #e5e7eb;
        border-radius: 10px;
        font-size: 0.95rem;
        outline: none;
        transition: all 0.2s ease;
        background: #f9fafb;

        &:focus {
            border-color: rgba(130, 22, 146, 0.5);
            background: #fff;
            box-shadow: 0 0 0 4px rgba(130, 22, 146, 0.1);
        }
    }
`;

const SuccessMessage = styled.div`
    background-color: #f0fdf4;
    color: #166534;
    padding: 1rem;
    border-radius: 10px;
    border: 1px solid #bbf7d0;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    text-align: center;
`;

const ErrorMessage = styled.div`
    background-color: #fef2f2;
    color: #991b1b;
    padding: 1rem;
    border-radius: 10px;
    border: 1px solid #fecaca;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    text-align: center;
`;

const FooterLink = styled.button`
    background: none;
    border: none;
    display: block;
    width: 100%;
    text-align: center;
    margin-top: 1.5rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.8;
        text-decoration: underline;
    }
`;

export default ForgotPasswordPage;
